import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, Phone, Music, Drum, Clock, CheckCircle, ChevronDown, ChevronUp, X, Loader2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getPhoneDisplay, getPhoneTel } from '@/lib/contactInfo';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditableImage } from '@/components/edit/PageEditableImage';
import { useHero } from '@/hooks/useHero';
import { EditableList } from '@/components/edit/EditableList';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon map so admins can swap feature badge icons via a string
const BADGE_ICONS = { CheckCircle, Clock, Music, Drum, Wrench, Phone };
const BadgeIcon = (name) => BADGE_ICONS[name] || CheckCircle;

const DrumRepairInner = () => {
  const hero = useHero('drum-repair');
  const { enabled: editEnabled, getValue } = usePageEditMode();
  const [expandedSection, setExpandedSection] = useState('samba');
  const [selectedService, setSelectedService] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [booking, setBooking] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const sambaRepairs = [
    { id: 'surdo-you-supply', name: 'Surdo repair (you supply head)', price: 30 },
    { id: 'repinique-you-supply', name: 'Repinique repair (you supply head)', price: 30 },
    { id: 'caixa-you-supply', name: 'Caixa repair (you supply head)', price: 30 },
    { id: 'caixa-we-supply', name: 'Caixa repair (we supply head)', price: 60 },
    { id: 'repinique-we-supply', name: 'Repinique (we supply head)', price: 60 },
    { id: 'atabaque-we-supply', name: 'Atabaque repair (we supply head)', price: 100 },
    { id: 'atabaque-you-supply', name: 'Atabaque repair (you supply head)', price: 130 },
    { id: 'tune-samba', name: 'Tune your Samba drum', price: 30 },
  ];

  const handDrumRepairs = [
    { id: 'conga-you-supply', name: 'Conga repair (you supply head)', price: 40 },
    { id: 'conga-we-supply', name: 'Conga repair (we supply head)', price: 100 },
    { id: 'djembe-you-supply', name: 'Djembe repair (you supply head)', price: 100 },
    { id: 'djembe-we-supply', name: 'Djembe repair (we supply head)', price: 150 },
    { id: 'pandeiro-we-supply', name: 'Pandeiro repair (we supply head)', price: 65 },
    { id: 'pandeiro-you-supply', name: 'Pandeiro repair (you supply head)', price: 30 },
    { id: 'cuica-we-supply', name: 'Cuica repair (we supply head)', price: 65 },
    { id: 'cuica-you-supply', name: 'Cuica repair (you supply head)', price: 30 },
    { id: 'tune-hand', name: 'Tune your hand drum', price: 35 },
  ];

  const privateClasses = [
    { id: 'private-capoeira', name: 'Private Capoeira Class', price: 40 },
    { id: 'private-samba-dance', name: 'Private Samba Dance Class', price: 40 },
    { id: 'private-samba-drumming', name: 'Private Samba Drumming Class', price: 40 },
    { id: 'private-atabaque', name: 'Private Atabaque/Conga/Djembe Class', price: 40 },
    { id: 'private-pandeiro', name: 'Private Pandeiro/Berimbau Class', price: 40 },
    { id: 'private-percussion', name: 'Private Samba Percussion Class', price: 40 },
  ];

  const workshop = { id: 'workshop', name: 'Drum Workshop: Repair & Making', price: 240 };

  const galleryImages = [
    '/api/media/casa-samba/library/images/drum-repair/img-0083.jpeg',
    '/api/media/casa-samba/library/images/drum-repair/img-0090.jpeg',
    '/api/media/casa-samba/library/images/drum-repair/img-0065.jpeg',
    '/api/media/casa-samba/library/images/drum-repair/img-0166.jpeg',
    '/api/media/casa-samba/library/images/drum-repair/img-0378.jpeg',
  ];

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleBookService = (service) => {
    setSelectedService(service);
    setShowBookingModal(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!booking.customer_name || !booking.customer_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/services/book`, {
        service_id: selectedService.id,
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        customer_phone: booking.customer_phone,
        notes: booking.notes
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to process booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ServiceCard = ({ title, titlePath, icon: Icon, services, path, sectionKey, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-2xl overflow-hidden"
    >
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-bold text-foreground">
            <PageEditable path={titlePath} as="span" fallback={title} />
          </h3>
        </div>
        {expandedSection === sectionKey ? (
          <ChevronUp className="w-6 h-6 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-6 h-6 text-muted-foreground" />
        )}
      </button>

      {expandedSection === sectionKey && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="border-t border-border/30"
        >
          <div className="p-6">
            <EditableList
              path={path}
              fallback={services}
              emptyItem={() => ({ id: `new-service-${Date.now()}`, name: 'New service', price: 30 })}
              className="space-y-3"
              renderItem={(service, index) => (
                <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border/30 hover:border-primary/30 transition-colors gap-3">
                  <div className="flex-1 min-w-0">
                    <PageEditable
                      path={`${path}.${index}.name`}
                      as="span"
                      fallback={service.name}
                      className="text-foreground"
                    />
                    <div
                      className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground"
                      style={{ display: editEnabled ? undefined : 'none' }}
                    >
                      id: <PageEditable path={`${path}.${index}.id`} as="span" fallback={service.id} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-primary font-bold">
                      $<PageEditable path={`${path}.${index}.price`} as="span" fallback={String(service.price)} />.00
                    </span>
                    <button
                      onClick={() => handleBookService(service)}
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full text-sm font-semibold transition-colors flex items-center gap-2"
                      data-testid={`book-${service.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Book
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section - Full Image Display */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-12 rounded-3xl overflow-hidden"
        >
          {/* Full image - shows entire photo without cropping */}
          <PageEditableImage
            path="hero.image"
            fallback="https://www.casasamba.com/wp-content/uploads/2026/09/r21yof37_Curtis-Pierre-Photo-59.jpg"
            alt="Mestre Curtis Pierre Performing"
            className="w-full h-auto block"
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          
          {/* Text positioned at bottom of image */}
          <div className="absolute bottom-0 left-0 right-0 px-8 py-8 md:py-12">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Wrench className="w-8 h-8 text-primary" />
                <span className="text-sm font-bold text-primary uppercase tracking-wider">
                  <PageEditable path="hero.eyebrow" fallback={hero.kicker || "Professional Services"} />
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold font-display text-foreground mb-4">
                <PageEditable path="hero.title" fallback={hero.title_top || "Mestre Curtis"} />
                <span className="block text-primary"><PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Drum Repair"} /></span>
              </h1>
              <PageEditable
                path="hero.tagline"
                as="p"
                className="text-lg text-white/80 mb-6"
                fallback="Have Broken Drum? Will Repair!"
              />
              <a
                href={getPhoneTel()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                Call: {getPhoneDisplay()}
              </a>
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/50 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold font-display text-foreground mb-4">
            <PageEditable path="about.title" as="span" fallback="Expert Cultural Drum Repair" />
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              <PageEditable
                path="about.p1"
                as="span"
                multiline
                fallback="This is the place where you can get your drum repaired. Just about any drum can be fixed or repaired. Whether you want it traditionally repaired, repaired as a keepsake, or just repaired enough to get a good sound out of it - this is what we do."
              />
            </p>
            <p>
              <PageEditable
                path="about.p2"
                as="span"
                multiline
                fallback="Mestre Curtis Pierre has spent countless years repairing and fixing various cultural drums, giving him extensive knowledge about how to repair just about any drum. We work with synthetic drum heads, natural skin heads, and drums with wooden heads."
              />
            </p>
          </div>
          
          <EditableList
            path="about.badges"
            fallback={[
              { icon: 'CheckCircle', color: 'green-500', text: 'Overnight fixes available' },
              { icon: 'CheckCircle', color: 'green-500', text: 'Loaner drums available' },
              { icon: 'Clock', color: 'primary', text: 'Tuning may take 2 weeks' },
            ]}
            emptyItem={() => ({ icon: 'CheckCircle', color: 'green-500', text: 'New feature' })}
            className="grid md:grid-cols-3 gap-4 mt-6"
            renderItem={(badge, idx) => {
              const Icon = BadgeIcon(badge.icon);
              const colorMap = {
                primary: 'text-primary',
                'green-500': 'text-green-500',
                'red-500': 'text-red-500',
                'yellow-500': 'text-yellow-500',
                'blue-500': 'text-blue-500',
                'purple-500': 'text-purple-500',
                accent: 'text-accent',
              };
              const colorCls = colorMap[badge.color] || 'text-green-500';
              return (
                <div className="flex items-center gap-3 p-4 bg-background rounded-xl border border-border/30 h-full">
                  <Icon className={`w-6 h-6 ${colorCls}`} />
                  <div className="flex-1">
                    <PageEditable
                      path={`about.badges.${idx}.text`}
                      as="span"
                      fallback={badge.text}
                      className="text-foreground"
                    />
                    <div
                      className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1"
                      style={{ display: editEnabled ? undefined : 'none' }}
                    >
                      icon: <PageEditable path={`about.badges.${idx}.icon`} as="span" fallback={badge.icon || 'CheckCircle'} />
                      {' · '}
                      color: <PageEditable path={`about.badges.${idx}.color`} as="span" fallback={badge.color || 'green-500'} />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </motion.div>

        {/* Services Grid */}
        <h2 className="text-3xl font-bold font-display text-foreground mb-6">
          <PageEditable path="servicesHeading" as="span" fallback="Select Your Repair Service" />
        </h2>
        
        <div className="space-y-4 mb-8">
          <ServiceCard
            title="Samba Drum Repair"
            titlePath="services.samba.title"
            icon={Drum}
            services={sambaRepairs}
            path="services.samba.items"
            sectionKey="samba"
            color="from-primary to-accent"
          />
          
          <ServiceCard
            title="Hand Drum Repair (Conga, Djembe, etc.)"
            titlePath="services.hand.title"
            icon={Music}
            services={handDrumRepairs}
            path="services.hand.items"
            sectionKey="hand"
            color="from-purple-500 to-pink-500"
          />
          
          <ServiceCard
            title="Private Classes"
            titlePath="services.classes.title"
            icon={Music}
            services={privateClasses}
            path="services.classes.items"
            sectionKey="classes"
            color="from-green-500 to-teal-500"
          />
        </div>

        {/* Workshop Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/50 rounded-2xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                <PageEditable path="workshop.title" as="span" fallback="Drum Workshop: Repair & Making" />
              </h3>
              <p className="text-muted-foreground">
                <PageEditable
                  path="workshop.desc"
                  as="span"
                  multiline
                  fallback="Learn the art of drum repair and making in this comprehensive hands-on workshop."
                />
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary">
                  $<PageEditable path="workshop.price" as="span" fallback={String(workshop.price)} />
                </p>
                <p className="text-sm text-muted-foreground">
                  <PageEditable path="workshop.priceLabel" as="span" fallback="per session" />
                </p>
              </div>
              <button
                onClick={() => handleBookService({
                  ...workshop,
                  name: getValue('workshop.title') || workshop.name,
                  price: Number(getValue('workshop.price')) || workshop.price,
                })}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105 flex items-center gap-2"
                data-testid="book-workshop"
              >
                <ShoppingCart className="w-5 h-5" />
                <PageEditable path="workshop.cta" as="span" fallback="Book Now" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Gallery */}
        <h2 className="text-3xl font-bold font-display text-foreground mb-6">
          <PageEditable path="gallery.title" as="span" fallback="Our Work" />
        </h2>
        <EditableList
          path="gallery.images"
          fallback={galleryImages.map((src) => ({ src }))}
          emptyItem={() => ({ src: '' })}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8"
          renderItem={(item, index) => (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="aspect-square rounded-xl overflow-hidden border border-border/50"
            >
              <PageEditableImage
                path={`gallery.images.${index}.src`}
                fallback={item.src}
                alt={`Drum repair work ${index + 1}`}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
              />
            </motion.div>
          )}
        />

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-2xl p-8 text-center"
        >
          <h2 className="text-2xl font-bold font-display text-foreground mb-4">
            <PageEditable path="cta.title" as="span" fallback="Ready to Get Your Drum Repaired?" />
          </h2>
          <p className="text-muted-foreground mb-6">
            <PageEditable
              path="cta.body"
              as="span"
              multiline
              fallback="Select a service above to book online, or give us a call!"
            />
            <br />
            <span className="text-foreground font-semibold">
              <PageEditable path="cta.signature" as="span" fallback="- Mestre Curtis, Director and Founder" />
            </span>
          </p>
          <a
            href={getPhoneTel()}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg transition-all hover:scale-105"
          >
            <Phone className="w-6 h-6" />
            <PageEditable path="cta.callLabel" as="span" fallback="Call Now:" /> {getPhoneDisplay()}
          </a>
        </motion.div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowBookingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">Book Service</h3>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-6">
                <p className="text-foreground font-semibold">{selectedService.name}</p>
                <p className="text-2xl font-bold text-primary">${selectedService.price}.00</p>
              </div>

              <form onSubmit={handleSubmitBooking} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={booking.customer_name}
                    onChange={(e) => setBooking({ ...booking, customer_name: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={booking.customer_email}
                    onChange={(e) => setBooking({ ...booking, customer_email: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={booking.customer_phone}
                    onChange={(e) => setBooking({ ...booking, customer_phone: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                    placeholder="(504) 555-1234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Notes (describe your drum, issue, etc.)
                  </label>
                  <textarea
                    value={booking.notes}
                    onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary resize-none"
                    rows={3}
                    placeholder="e.g., Djembe with torn goatskin head, need traditional repair"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Proceed to Payment - ${selectedService.price}.00
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};



const DrumRepair = () => (
  <PageEditProvider slug="drum-repair">
    <DrumRepairInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export { DrumRepair };
export default DrumRepair;
